import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { BULK_STOCKTAKE_MAX_ROWS } from '@threadheads/shared';
import { toast } from '@/hooks/use-toast';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { downloadCsvFile } from '@/lib/download';
import { Product } from '../types';
import { useConnectedClients } from './useConnectedClients';
import {
  buildStocktakeCsv,
  buildExampleStocktakeCsv,
  buildStocktakeFilename,
  countSheetSize,
  isJustInTimePair,
  type StocktakeBlock,
  type StocktakeCsvSource,
} from '../utils/stocktakeCsv';

/**
 * useStocktakeExport — the one place that builds + downloads a stocktake
 * count sheet, for all three entry points (selected rows, all filtered rows,
 * blank example). Centralising it is what keeps the export-time row-count
 * warning and the wizard's import-time block in lockstep with the same
 * `BULK_STOCKTAKE_MAX_ROWS` constant, and what makes the cross-org /
 * no-location guards apply identically regardless of which button the
 * operator clicked.
 */
export function useStocktakeExport() {
  const { t } = useTranslation('inventory');
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const [includeCurrentCounts, setIncludeCurrentCounts] = useState(false);

  // Mirrors useClientOrgFilter's gate so the two hooks share a React Query
  // cache key (same `locationId`, same `enabled`) and only one network
  // request fires when both are mounted on the Inventory page at once.
  const sharedLocationsEnabled =
    (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? '') === 'true';

  const { clients: connectedClients } = useConnectedClients({
    locationId: selectedLocation?.id,
    enabled: sharedLocationsEnabled,
  });

  // Deliberately excludes the caller's own org: `ownerOrganizationId` is
  // populated on every row (including own-org rows), so if the own org were
  // in this map every own-org row would resolve to a name here instead of
  // falling through to `fallbackOwnerName` — printing a UI-only "(You)"
  // suffix onto a sheet meant to be walked around a warehouse and re-
  // imported. Leaving it out means own-org rows always take the plain
  // `fallbackOwnerName` path in `buildStocktakeCsv`.
  const ownerNameById = useMemo(() => {
    const map = new Map<string, string>();
    connectedClients.forEach((client) => map.set(client.id, client.name));
    return map;
  }, [connectedClients]);

  const labels = useMemo(
    () => ({
      headers: [
        t('stocktakeCsv.header.sku'),
        t('stocktakeCsv.header.stockCount'),
        t('stocktakeCsv.header.title'),
        t('stocktakeCsv.header.productionType'),
        t('stocktakeCsv.header.productType'),
        t('stocktakeCsv.header.size'),
        t('stocktakeCsv.header.color'),
        t('stocktakeCsv.header.owner'),
        t('stocktakeCsv.header.ownerId'),
        t('stocktakeCsv.header.location'),
      ],
      blockLabels: {
        POD: t('stocktakeCsv.productionType.pod'),
        PAP: t('stocktakeCsv.productionType.pap'),
        PREPRINTED: t('stocktakeCsv.productionType.preprinted'),
        TRIM: t('stocktakeCsv.productionType.trim'),
      } satisfies Record<StocktakeBlock, string>,
    }),
    [t],
  );

  const buildAndDownload = useCallback(
    (rows: StocktakeCsvSource[], variant: 'all' | 'selected' | 'example', leftOutJustInTime = 0) => {
      try {
        const locationName = selectedLocation?.name ?? '';
        const options = {
          labels,
          locationName,
          ownerNameById,
          fallbackOwnerName: organization?.name ?? '',
          ownOrganizationId: organization?.id ?? '',
          includeCurrentCounts,
        };

        const content =
          variant === 'example'
            ? buildExampleStocktakeCsv(options)
            : buildStocktakeCsv(rows, options);

        const today = new Date().toISOString().split('T')[0] ?? '';
        const filename = buildStocktakeFilename(locationName, variant, today);

        downloadCsvFile(content, filename);

        toast({
          title: t('stocktakeExport.successTitle'),
          description:
            variant === 'example'
              ? t('stocktakeExport.exampleDesc')
              : [
                  // The file's own line count (aliases out, shared pairs as one line), as the menu says.
                  t('stocktakeExport.successDesc', { count: countSheetSize(rows).lines, filename }),
                  leftOutJustInTime > 0 ? t('supply.bulkSkipped', { count: leftOutJustInTime }) : null,
                ]
                  .filter(Boolean)
                  .join(' '),
        });
      } catch (err) {
        toast({
          title: t('stocktakeExport.failedTitle'),
          description: t('stocktakeExport.failedDesc'),
          variant: 'destructive',
        });

        Sentry.captureException(err, {
          tags: { component: 'useStocktakeExport', operation: 'exportCountSheet' },
          extra: { variant, rowCount: rows.length },
        });
      }
    },
    [labels, selectedLocation, ownerNameById, organization, includeCurrentCounts, t],
  );

  const exportRows = useCallback(
    (rows: Product[], variant: 'all' | 'selected') => {
      // In All-Locations mode `getProducts` returns stock SUMMED across
      // locations; a sheet of org-wide totals re-imported at one location
      // would set that location's stock to the org total. The blank example
      // sheet carries no real stock data, so it is not subject to this guard.
      if (!selectedLocation) {
        toast({
          title: t('stocktakeExport.noLocationTitle'),
          description: t('stocktakeExport.noLocationDesc'),
          variant: 'destructive',
        });
        return;
      }

      if (rows.length === 0) {
        toast({
          title: t('stocktakeExport.emptyTitle'),
          description: t('stocktakeExport.emptyDesc'),
        });
        return;
      }

      // BLU-1268: a Just in time pair has no shelf count to take (its stock
      // figure is the store constant). It is left out here, so every count
      // below and the toast describe the sheet that is actually written, and
      // the operator is told how many were left out.
      const sheetRows = rows.filter((row) => !isJustInTimePair(row));
      const leftOutJustInTime = rows.length - sheetRows.length;
      if (sheetRows.length === 0) {
        toast({
          title: t('stocktakeExport.emptyTitle'),
          description: t('supply.bulkSkipped', { count: leftOutJustInTime }),
        });
        return;
      }

      // Non-blocking: the file still exports over the limit, matching the
      // wizard's own BULK_STOCKTAKE_MAX_ROWS ceiling — the operator can split
      // it before re-uploading, but is not blocked from taking the count.
      const sheetLines = countSheetSize(sheetRows).lines;
      if (sheetLines > BULK_STOCKTAKE_MAX_ROWS) {
        toast({
          title: t('stocktakeExport.overLimitTitle'),
          description: t('stocktakeExport.overLimitDesc', {
            count: sheetLines,
            limit: BULK_STOCKTAKE_MAX_ROWS,
          }),
        });
      }

      // Warn on ANY foreign-owned row, not "more than one distinct owner":
      // with a client filter active the sheet can be 100% client-owned, and
      // a mixture check would never fire.
      const ownOrgId = organization?.id;
      const crossOrgCount = sheetRows.filter(
        (row) => row.ownerOrganizationId !== undefined && row.ownerOrganizationId !== ownOrgId,
      ).length;
      if (crossOrgCount > 0) {
        toast({
          title: t('stocktakeExport.crossOrgTitle'),
          description: t('stocktakeExport.crossOrgDesc', { count: crossOrgCount }),
        });
      }

      buildAndDownload(sheetRows, variant, leftOutJustInTime);
    },
    [selectedLocation, organization, buildAndDownload, t],
  );

  const exportExample = useCallback(() => {
    buildAndDownload([], 'example');
  }, [buildAndDownload]);

  return {
    exportRows,
    exportExample,
    includeCurrentCounts,
    setIncludeCurrentCounts,
  };
}
